import { styled, Typography } from '@mui/material';
import type { ReactNode } from 'react';

import { Stack } from '../../atoms/Stack';

type Props = {
  title: string;
  subtitle?: string;
  /** Illustration (SVG or image), stretched up to 240px wide. */
  image?: ReactNode;
  /** A single icon, kept at a fixed modest size (use instead of `image` when there is no illustration). */
  icon?: ReactNode;
  /** Recovery action, e.g. a text `Button` clearing the filters. */
  action?: ReactNode;
  'data-testid'?: string;
};

const ILLUSTRATION_MAX_WIDTH = 240;
const ICON_SIZE = 5;
const TEXT_MAX_WIDTH = '40ch';

const EmptyStateRoot = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  textAlign: 'center',
  paddingBlock: theme.spacing(4),
  paddingInline: theme.spacing(2),
}));

const Illustration = styled('div')(({ theme }) => ({
  width: '100%',
  maxWidth: ILLUSTRATION_MAX_WIDTH,
  marginBottom: theme.spacing(3),
  '& > svg, & > img': {
    display: 'block',
    width: '100%',
    height: 'auto',
  },
}));

const IconSlot = styled('div')(({ theme }) => ({
  display: 'flex',
  marginBottom: theme.spacing(1.5),
  '& > svg': {
    fontSize: theme.spacing(ICON_SIZE),
  },
}));

const Text = styled('div')({
  maxWidth: TEXT_MAX_WIDTH,
});

const Action = styled('div')(({ theme }) => ({
  marginTop: theme.spacing(2),
}));

export function EmptyState(props: Props) {
  return (
    <EmptyStateRoot data-testid={props['data-testid'] ?? 'empty-state'}>
      {props.image ? <Illustration aria-hidden="true">{props.image}</Illustration> : null}
      {!props.image && props.icon ? <IconSlot aria-hidden="true">{props.icon}</IconSlot> : null}
      <Text>
        <Stack spacing={0.5}>
          <Typography variant="h6" component="p">{props.title}</Typography>
          {props.subtitle ? <Typography variant="body2">{props.subtitle}</Typography> : null}
        </Stack>
      </Text>
      {props.action ? <Action>{props.action}</Action> : null}
    </EmptyStateRoot>
  );
}

export type { Props as EmptyStateProps };
